/**
 * The active locale, and the writing direction derived from it.
 *
 * Authority: bead scope-njw; PRD §8 (Arabic localisation deferred, Q8).
 *
 * v1 ships one locale, and `direction` always reads `ltr`. The derivation is
 * still real: a code path that has only ever produced one value is a code path
 * that exists, and the day `ar` is added the only thing that changes is this
 * file's `SUPPORTED_LOCALES`. The alternative — hard-coding `dir="ltr"` in
 * index.html — is not a shorter version of this, it is the absence of it, and
 * it is found later by an Arabic page that lays out backwards.
 */

/** BCP 47 tags this build ships strings for. */
export const SUPPORTED_LOCALES = ['en'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: SupportedLocale = 'en'

export type Direction = 'ltr' | 'rtl'

/**
 * Right-to-left languages, by primary subtag. The table is complete for the
 * languages NIMER operates in and is the thing to extend — not a `locale ===
 * 'ar'` test written at whichever call site needed it first.
 */
const RTL_LANGUAGES = new Set(['ar', 'arc', 'dv', 'fa', 'he', 'ku', 'ps', 'ur', 'yi'])

export function directionOf(locale: string): Direction {
  const language = locale.toLowerCase().split(/[-_]/, 1)[0] ?? ''

  return RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr'
}

export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale)
}

/**
 * The locale this session runs in. Negotiation against the browser's languages
 * arrives with the second locale; until then the answer is the default, and it
 * is reached through this function so the call sites do not have to change.
 */
export function activeLocale(): SupportedLocale {
  return DEFAULT_LOCALE
}
